import React from "react";
import { UserContext } from "./context";
import { UserContextType } from "./interface";
import { UserModel, getUser as requestUser } from "../../backend";
import { getUserPermissions, getUserRoleName } from "./utils";

export const UserProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [loggedIn, setLoggedIn] = React.useState<boolean>(false);
  const [pending, setPending] = React.useState<boolean>(true);
  const [user, setUser] = React.useState<UserModel | null>(null);

  const getUser = React.useCallback(async (): Promise<UserModel | null> => {
    const res = await requestUser();
    if (res.ok && res.data) {
      setUser(res.data);
      setLoggedIn(true);
      return res.data;
    } else if (res.status === 401 || res.status === 403) {
      setUser(null);
      setLoggedIn(false);
    }
    // For 429, 5xx, or network errors — preserve current auth state
    return null;
  }, []);

  React.useEffect(() => {
    if (!loggedIn) {
      setUser(null);
    }
  }, [loggedIn]);

  // Initial auth check on mount (cookie-based session)
  React.useEffect(() => {
    getUser().then(() => setPending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user data after login (setLoggedIn called externally)
  React.useEffect(() => {
    if (loggedIn && !user) {
      getUser();
    }
  }, [loggedIn, user, getUser]);

  const value = React.useMemo<UserContextType>(() => {
    const permissions = user ? getUserPermissions(user) : [];
    const role = user ? getUserRoleName(user) : "";
    return {
      getUser,
      loggedIn,
      pending,
      permissions,
      role,
      setLoggedIn,
      user,
    };
  }, [loggedIn, user, getUser, pending]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
